"use client";

import { ChevronRight } from "lucide-react";
import { LeaderCard } from "./leader-card";
import Image from "next/image";
import { useEffect, useState, useRef } from "react";

interface VotingData {
  name: string;
  vote: number;
  token_id: string;
  id: number;
  image_url: string;
  description: string;
  twitter_account_link: string;
  website_url: string;
  telegram_channel_link: string;
  buy_url: string;
}

export default function LBoard() {
  const [allWinnerData, setAllWinnerData] = useState<VotingData[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Client-side lazy loading state
  const itemsPerPage = 20;
  const [visibleCount, setVisibleCount] = useState(itemsPerPage);
  const [loading, setLoading] = useState(true);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Fetch all data once
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          "https://api.catdevai.com/api/latest-winner/"
        );
        const data = await response.json();
        setAllWinnerData(data.last_winner);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
      setLoading(false);
    };

    fetchData();
  }, []);

  // Filter and sort data based on user input
  const filteredData = allWinnerData.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredAndSortedData = [...filteredData].sort((a, b) =>
    sortOrder === "asc" ? a.id - b.id : b.id - a.id
  );
  const displayedData = filteredAndSortedData.slice(0, visibleCount);

  // Intersection observer to increase visible count when the sentinel is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          visibleCount < filteredAndSortedData.length
        ) {
          setVisibleCount((prev) => prev + itemsPerPage);
        }
      },
      { root: null, rootMargin: "0px", threshold: 1.0 }
    );
    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }
    return () => observer.disconnect();
  }, [visibleCount, filteredAndSortedData]);

  return (
    <section className="py-4 pt-20 md:py-20 px-4">
      {/* Top Decorative Images */}
      <div className="flex justify-between md:px-10">
        <Image
          src="/assests/cat_top_left.png"
          alt="Decorative cat"
          width={200}
          height={200}
          className="animate-floattopleft w-16 h-16 md:w-44 md:h-44 -rotate-[23.26deg]"
        />
        <Image
          src="/assests/cat_top_right.png"
          alt="Decorative cat"
          width={200}
          height={200}
          className="animate-floattopright w-20 h-20 md:w-56 md:h-56"
        />
      </div>

      {/* Header */}
      <div className="text-center mb-16 md:mb-0 md:-translate-y-20 md:w-[70%] mx-auto md:space-y-0 flex flex-col items-center justify-center ">
        <h4 className="bg-indigo-50 drop-shadow-md rounded-full w-fit text-sm px-4 py-1 text-indigo-700 text-center flex items-center">
          Explore Trending Tokens <ChevronRight size={18} />
        </h4>
        <h2 className="font-['PoetsenOne'] text-3xl md:text-5xl lg:text-6xl font-bold  text-transparent bg-clip-text flex items-center justify-center py-4 relative">
          <span className="relative inline-block">
            <span className="absolute inset-0  text-indigo-950/95">
              Leader Board
            </span>
            <span className="relative">Leader Board</span>
          </span>
        </h2>
        <p className="text-gray-700 max-w-screen-sm md:px-10">
          Welcome to the Leader Board, Scroll, vote, and use the search bar to
          find your favorites legends carve the path to your next big meowment.
        </p>

        <div className="bg-gradient-to-b from-indigo-300 to-white/0 rounded-t-full h-96 w-full max-w-screen-md absolute -z-10"></div>
      </div>

      {/* Search Section */}
      <div className="sticky top-24 z-10 flex justify-center mb-10 mt-8 pb-12">
        <div className="relative flex items-center max-w-screen-md w-full bg-white rounded-full shadow-lg border border-indigo-100 overflow-hidden">
          <div className="absolute left-4 text-gray-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search tokens..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              // Reset visible count when search changes
              setVisibleCount(itemsPerPage);
            }}
            className="pl-12 pr-4 py-3 w-full bg-transparent focus:outline-none font-['Syne'] text-gray-700"
          />
          <button
            onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
            className="bg-indigo-50/50 hover:bg-indigo-100 transition-colors p-3.5 text-indigo-600"
            aria-label={
              sortOrder === "asc"
                ? "Sort by Oldest First"
                : "Sort by Newest First"
            }
          >
            {sortOrder === "asc" ? (
              <div className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"
                  />
                </svg>
                <span className="ml-1 text-xs font-medium">Newest</span>
              </div>
            ) : (
              <div className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4"
                  />
                </svg>
                <span className="ml-1 text-xs font-medium">Oldest</span>
              </div>
            )}
          </button>
        </div>
      </div>

      {displayedData.length === 0 && !loading && (
        <div className="text-center text-gray-500 py-8 mb-8">
          No tokens found matching your search.
        </div>
      )}

      {/* Cards Section */}
      <div className="container mx-auto mt-10">
        <div className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-x-2 gap-y-12 place-items-center z-10 max-w-6xl mx-auto justify-items-center">
          {displayedData.map((item) => (
            <LeaderCard
              key={item.id}
              imageSrc={item.image_url}
              content={item.description}
              name={item.name}
              twitter={item.twitter_account_link}
              telegram={item.telegram_channel_link}
              website={item.website_url}
              buy={item.buy_url}
            />
          ))}
        </div>
        {/* Sentinel element to trigger lazy loading */}
        {/* <div ref={sentinelRef} className="py-4">
          {loading && (
            <div className="text-center text-gray-600">Loading...</div>
          )}
        </div> */}
        <div
          ref={sentinelRef}
          className="py-4 flex justify-center items-center"
        >
          {loading && (
            <div className="flex items-center space-x-2">
              <svg
                className="animate-spin h-6 w-6 text-indigo-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                ></path>
              </svg>
              <span className=" text-2xl font-medium text-indigo-500">
                Loading...
              </span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
